import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { LogOut } from "lucide-react";
import { motion } from "framer-motion";
import { useAuth } from "@/lib/auth";
import { useCms } from "@/lib/cms";
import { embedMotionProps, useEmbedPreview } from "@/lib/embedPreview";
import BrandLogo from "@/components/BrandLogo";
import NotificationBell from "@/components/NotificationBell";
import { adminNotificationHref } from "@/lib/adminNotificationTarget";
import HeaderNav from "@/components/HeaderNav";
import HeaderMobileMenu from "@/components/HeaderMobileMenu";
import HeaderTextLink from "@/components/HeaderNavLink";
import HeaderBilingualLabel from "@/components/HeaderBilingualLabel";
import { openContactForm } from "@/lib/contactForm";
import "./Header.css";

import { visibleSiteNav } from "@/lib/publicNav";

const previewNavItems = visibleSiteNav().map((item) => ({
  label: item.en,
  page: item.page,
  action: item.action,
}));

function navHref(item) {
  const page = item.page || "/";
  if (!item.sectionId) return page;
  return `${page}#${item.sectionId}`;
}

function scrollToSection(sectionId) {
  document.getElementById(sectionId)?.scrollIntoView({ behavior: "smooth" });
  const hash = `#${sectionId}`;
  if (window.location.hash !== hash) {
    window.history.replaceState(null, "", hash);
  }
}

export default function Header({ previewDevice, embedMode = false }) {
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, isAdmin, isCustomer, user, logout, loading } = useAuth();
  const { cms } = useCms();
  const embedFromUrl = useEmbedPreview();
  const embedModeActive = embedMode || embedFromUrl;

  const handleLogoClick = (e) => {
    e.preventDefault();
    const onHome = location.pathname === "/" && !location.search;
    if (onHome) {
      if (location.hash) {
        window.history.replaceState(null, "", "/");
      }
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
      return;
    }
    navigate("/");
  };

  const handleCustomerNotificationClick = (n) => {
    const link = n?.link || "";
    const cat = n?.category;
    if (cat === "quote" || link.includes("quotes")) {
      navigate("/mypage?tab=quotes");
      return;
    }
    if (cat === "contract" || link.includes("contracts")) {
      navigate("/mypage?tab=contracts");
      return;
    }
    if (cat === "inquiry" || link.includes("inquiries")) {
      navigate("/mypage?tab=inquiries");
      return;
    }
    if (cat === "project" || link.includes("projects")) {
      navigate("/mypage?tab=projects");
      return;
    }
    navigate("/mypage");
  };

  const handleAdminNotificationClick = (n) => {
    navigate(adminNotificationHref(n));
  };

  const handleSectionNav = (e, item) => {
    e.preventDefault();

    const page = item.page || "/";
    const { sectionId } = item;

    const go = () => {
      if (!sectionId) {
        if (location.pathname === page) {
          window.scrollTo({ top: 0, behavior: "smooth" });
          window.history.replaceState(null, "", page);
          return;
        }
        navigate(page);
        return;
      }

      if (location.pathname === page) {
        scrollToSection(sectionId);
        window.history.replaceState(null, "", `${page}#${sectionId}`);
        return;
      }
      navigate(`${page}#${sectionId}`);
    };

    requestAnimationFrame(() => requestAnimationFrame(go));
  };

  useEffect(() => {
    let rid;
    let lastScrolled = window.scrollY > 16;

    const onScroll = () => {
      cancelAnimationFrame(rid);
      rid = requestAnimationFrame(() => {
        const next = window.scrollY > 16;
        if (next !== lastScrolled) {
          lastScrolled = next;
          setScrolled(next);
        }
      });
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(rid);
    };
  }, []);

  const headerSurfaceClass = scrolled
    ? "bg-black/90 border-b border-white/10"
    : "bg-transparent border-b border-transparent";

  const headerClassName = embedModeActive
    ? `site-header-root ds-header-root ds-header-root--embed relative z-50 w-full transition-all duration-300 ${headerSurfaceClass}`
    : `site-header-root ds-header-root fixed top-0 left-0 right-0 z-50 w-full transition-all duration-300 ${headerSurfaceClass}`;

  const headerInner = (
    <>
      <div className="ds-header-bar">
        <div className="ds-header-bar__side ds-header-bar__side--left">
          <BrandLogo cms={cms} linkTo="/" onClick={handleLogoClick} size="md" />
        </div>

        <div className="ds-header-bar__center">
          {!embedModeActive ? (
            <>
              <nav className="ds-header-nav ds-header-nav--desktop-inline" aria-label="주요 메뉴">
                <HeaderNav />
              </nav>
              <HeaderMobileMenu />
            </>
          ) : (
            <nav
              className={`ds-header-preview-nav flex items-center gap-3 ${
                previewDevice === "desktop" ? "" : "hidden"
              }`}
            >
              {previewNavItems.map((item) =>
                item.action === "contact" ? (
                  <button
                    key={item.label}
                    type="button"
                    className="text-[0.625rem] text-white/70 whitespace-nowrap"
                    onClick={openContactForm}
                  >
                    {item.label}
                  </button>
                ) : (
                  <a
                    key={item.sectionId || item.label}
                    href={navHref(item)}
                    className="text-[0.625rem] text-white/70 whitespace-nowrap"
                    onClick={(e) => handleSectionNav(e, item)}
                  >
                    {item.label}
                  </a>
                ),
              )}
            </nav>
          )}
        </div>

        <div className="ds-header-bar__side ds-header-bar__side--right">
          {!embedModeActive ? (
            <>
              {location.pathname.startsWith("/admin") ? null : (
                <>
                  {loading ? null : isAuthenticated ? (
                    <>
                      {isAdmin ? (
                        <>
                          <NotificationBell scope="admin" onItemClick={handleAdminNotificationClick} />
                          <Link
                            to="/admin"
                            data-testid="header-admin-dashboard-link"
                            className="hidden lg:inline text-sm text-white/70 hover:text-white transition-colors"
                          >
                            관리자
                          </Link>
                        </>
                      ) : null}
                      {isCustomer ? (
                        <>
                          <NotificationBell scope="customer" onItemClick={handleCustomerNotificationClick} />
                          <Link
                            to="/mypage"
                            data-testid="header-mypage-link"
                            className="hidden lg:inline text-sm text-white/70 hover:text-white transition-colors"
                          >
                            마이페이지
                          </Link>
                        </>
                      ) : null}
                      <div
                        className="ds-header-auth-mobile-only ds-header-auth-mobile-only--logged-in"
                        data-testid="header-auth-mobile-logged-in"
                      >
                        {isAdmin ? (
                          <Link
                            to="/admin"
                            data-testid="header-admin-dashboard-link-mobile"
                            className="ds-header-text-link"
                          >
                            관리자
                          </Link>
                        ) : null}
                        {isCustomer ? (
                          <Link
                            to="/mypage"
                            data-testid="header-mypage-link-mobile"
                            className="ds-header-text-link"
                          >
                            마이페이지
                          </Link>
                        ) : null}
                        <button
                          type="button"
                          onClick={logout}
                          data-testid="header-logout-button-mobile"
                          className="ds-header-text-link"
                        >
                          로그아웃
                        </button>
                      </div>
                      <button
                        onClick={logout}
                        data-testid="header-logout-button"
                        className="ds-header-text-link hidden lg:inline-flex items-center gap-1.5"
                      >
                        <LogOut size={12} aria-hidden="true" />
                        <HeaderBilingualLabel en="Logout" ko="로그아웃" />
                      </button>
                    </>
                  ) : (
                    <>
                      <div
                        className="ds-header-auth-mobile-only"
                        data-testid="header-auth-mobile"
                      >
                        <HeaderTextLink
                          to="/login"
                          as={Link}
                          en="Login"
                          ko="로그인"
                          testId="header-login-link-mobile"
                          className="ds-header-text-link"
                        />
                        <HeaderTextLink
                          href="/signup"
                          en="Sign Up"
                          ko="회원가입"
                          testId="header-signup-link-mobile"
                          className="ds-header-text-link"
                          onClick={(e) => {
                            e.preventDefault();
                            window.location.href = "/signup";
                          }}
                        />
                      </div>
                      <HeaderTextLink
                        to="/login"
                        as={Link}
                        en="Login"
                        ko="로그인"
                        testId="header-login-link"
                        className="ds-header-preview-auth ds-header-auth-desktop hidden md:inline"
                      />
                      <HeaderTextLink
                        to="/signup"
                        as={Link}
                        en="Sign Up"
                        ko="회원가입"
                        testId="header-signup-link"
                        className="ds-header-preview-auth ds-header-auth-desktop hidden md:inline"
                      />
                    </>
                  )}
                </>
              )}
            </>
          ) : (
            <div
              className={`ds-header-preview-actions flex items-center gap-2 ${
                previewDevice === "desktop" ? "" : "hidden"
              }`}
            >
              <span className="btn-primary text-[0.625rem] px-3 py-1.5">상담 신청</span>
            </div>
          )}
        </div>
      </div>
    </>
  );

  if (embedModeActive) {
    return (
      <header data-testid="site-header" className={headerClassName}>
        {headerInner}
      </header>
    );
  }

  return (
    <motion.header
      data-testid="site-header"
      {...embedMotionProps(false, {
        initial: { y: -40, opacity: 0 },
        animate: { y: 0, opacity: 1 },
        transition: { duration: 0.5 },
      })}
      className={headerClassName}
    >
      {headerInner}
    </motion.header>
  );
}
